// IntroSequenceTimeline.tsx - the Intro Sequences mode: a 2D sibling to
// CutsceneTimeline.tsx (this repo's own Cutscenes mode), authoring
// `@sstk/intro-kit`'s `IntroSequence` schema (a pure, time-keyed timeline of
// sprite/text/particle layers, sampled by pure functions - see that
// package's own schema.ts for the full design rationale) instead of
// build-kit's 3D `Cutscene`. Deliberately mirrors CutsceneTimeline's
// structure: the same "call the pure sampling functions directly, not the
// runtime player" preview approach, the same M2Panel/M2Label layout, the
// same debounced mode-document persistence - but with a variable-length
// `layers` list standing in for Cutscene's five fixed tracks, so this file
// also owns layer add/remove/reorder/field-editing that the cutscene
// schema's fixed shape never needed.
//
// The project has no generic asset browser yet, so the sprite picker below
// reads straight out of the project's own 'pixel' mode document (the same
// source Pixel Studio's Place-mode sprite list reads from, editor-pixel.tsx)
// and converts a chosen sprite/clip into `@sstk/intro-kit`'s baked frame
// shape itself. Left dimension-ungated (unlike `cutscene`/`pixel`), since
// text and particle layers work with no sprite art at all - a 3D project can
// still author a text/particle boot sequence with zero Pixel Studio sprites.
//
// Persists through the mode-document store as kind 'introsequences'
// (IntroSequenceLibraryDoc). Exports: IntroRoute.
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  buildParticleSeeds,
  DEFAULT_TRANSFORM,
  paintIntroFrame,
  sampleLayerTransform,
  validateIntroSequence,
  type AudioCue,
  type BakedSpriteClip,
  type Easing,
  type FillLayer,
  type IntroLayer,
  type IntroSequence,
  type ParticleLayer,
  type ParticlePreset,
  type ParticleSeed,
  type SampledTransform,
  type SpriteAsset,
  type SpriteLayer,
  type TextLayer,
  type TransformKey,
} from '@sstk/intro-kit';
import { EdConfirmDialog } from './editor-panels';
import { FieldNumber, FieldSelect, FieldText } from './fieldPrimitives';
import { M2Label, M2Panel } from './editor-modes2';
import { useModeDocument } from './useModeDocument';
import { uniqueId } from './CutsceneTimeline';
import type { PixelSpriteDoc } from './editor-pixel';

/** The 'introsequences' mode document: every authored intro sequence in the
 * project, plus the runtime canvas resolution each one exports at.
 * `IntroSequence` itself (an `@sstk/intro-kit` schema type) deliberately has
 * no width/height - the schema is resolution-agnostic - so this wrapper owns
 * it, keyed by sequence id since a sequence's own duration/skippable/layers
 * live inside the schema type but its export resolution does not. */
export type IntroSequenceLibraryDoc = {
  sequences: IntroSequence[];
  canvasSize: Record<string, { width: number; height: number }>;
};
export const INTRO_LIBRARY_DOC_FALLBACK: IntroSequenceLibraryDoc = { sequences: [], canvasSize: {} };

export const DEFAULT_INTRO_CANVAS_SIZE = { width: 480, height: 270 };

/** Read-only slice of the 'pixel' mode document this mode needs: the
 * project's own baked sprite art. No generic asset browser exists yet in
 * this app - this is the sprite picker until one does. */
type PixelSpriteSource = {
  sprites: Record<string, PixelSpriteDoc>;
};
const PIXEL_SPRITE_SOURCE_FALLBACK: PixelSpriteSource = { sprites: {} };

export function blankIntroSequence(id: string): IntroSequence {
  return { id, duration: 3, skippable: true, layers: [], tracks: { transform: {}, audio: [] } };
}

/** Converts one named clip out of a project's own Pixel Studio sprite doc
 * into the baked shape `paintIntroFrame` reads. Always emits a
 * `BakedSpriteClip` (even for a single-frame clip) rather than special-casing
 * a 1-frame clip down to a bare `BakedSpriteFrame` - simpler, and
 * `resolveSpriteFrame` already handles a 1-frame clip correctly. */
function bakePixelClip(sprite: PixelSpriteDoc, clipName: string): BakedSpriteClip | null {
  const clip = sprite.clips[clipName];
  if (!clip) return null;
  return {
    frames: clip.frames.map((cells) => ({ size: sprite.size, cells })),
    fps: clip.fps,
    loop: clip.loop,
  };
}

/** Every baked sprite asset a sequence's sprite layers actually reference,
 * resolved from the project's pixel doc - the one conversion shared by both
 * the live preview and "Download JSON", so the exported artifact and the
 * on-screen preview can never silently disagree. A sprite layer whose
 * `sprite.clip` is unset (or names a clip the sprite no longer has) falls
 * back to that sprite's own `activeClip`, matching the picker's own default. */
function resolveFrames(sequence: IntroSequence, sprites: Record<string, PixelSpriteDoc>): Record<string, SpriteAsset> {
  const frames: Record<string, SpriteAsset> = {};
  for (const layer of sequence.layers) {
    if (layer.kind !== 'sprite') continue;
    const sprite = sprites[layer.sprite.spriteId];
    if (!sprite) continue;
    const clipName = layer.sprite.clip && sprite.clips[layer.sprite.clip] ? layer.sprite.clip : sprite.activeClip;
    const baked = bakePixelClip(sprite, clipName);
    if (baked) frames[layer.sprite.spriteId] = baked;
  }
  return frames;
}

function sampleAllTransforms(sequence: IntroSequence, t: number): Record<string, SampledTransform> {
  const transforms: Record<string, SampledTransform> = {};
  for (const layer of sequence.layers) transforms[layer.id] = sampleLayerTransform(sequence, layer.id, t);
  return transforms;
}

/** Serialises the exact artifact shape Furrow, Nine Lives, Dwindling and
 * Mewsli are being wired to consume as a static checked-in asset: `sequence`
 * (this mode's own authored `IntroSequence`), `frames` (every sprite layer's
 * referenced art resolved from this project's Pixel Studio sprites, the same
 * resolution the live preview paints from), and the author-chosen
 * `width`/`height` the runtime `IntroPlayer` canvas should be created at.
 * Field names are a fixed external contract - do not rename them. */
function downloadIntroSequenceJson(
  sequence: IntroSequence,
  sprites: Record<string, PixelSpriteDoc>,
  canvasSize: { width: number; height: number },
): void {
  const payload = {
    sequence,
    frames: resolveFrames(sequence, sprites),
    width: canvasSize.width,
    height: canvasSize.height,
  };
  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${sequence.id}.intro.json`;
  a.click();
  URL.revokeObjectURL(url);
}

/* ── Preview: a plain 2D <canvas>, painted by calling `sampleLayerTransform`
 * per layer and `paintIntroFrame` directly - not `@sstk/intro-kit/react`'s
 * `IntroPlayer`, which is `requestAnimationFrame`-driven for monotonic live
 * playback and has no notion of an arbitrary scrub. This is the same
 * "sample the pure functions yourself" approach CutsceneTimeline's own
 * preview already uses for `Cutscene`. ─────────────────────────────────── */

function IntroPreviewCanvas({
  sequence, frames, t, width, height,
}: {
  sequence: IntroSequence;
  frames: Record<string, SpriteAsset>;
  t: number;
  width: number;
  height: number;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // Rebuilt whenever the layer list changes (coarser than "only when a
  // particle layer's own preset/seed/count changes", but still far cheaper
  // than "every scrub tick" - `t` is deliberately not a dependency here).
  // `buildParticleSeeds` is deterministic but the package's own docs are
  // explicit that it should be called once per particle layer and reused,
  // not rebuilt every frame.
  const particleSeeds = useMemo(() => {
    const map = new Map<string, ParticleSeed[]>();
    for (const layer of sequence.layers) {
      if (layer.kind === 'particles') map.set(layer.id, buildParticleSeeds(layer.preset, layer.seed, layer.count));
    }
    return map;
  }, [sequence.layers]);

  useEffect(() => {
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    paintIntroFrame(ctx, sequence, t, { transforms: sampleAllTransforms(sequence, t) }, frames, particleSeeds, width, height);
  }, [sequence, frames, particleSeeds, t, width, height]);

  return (
    <canvas
      ref={canvasRef}
      width={width}
      height={height}
      style={{
        width: '100%', maxWidth: width, display: 'block', margin: '0 auto', borderRadius: 10,
        border: '1px solid var(--ss-border-soft)', background: '#0a0a0c', imageRendering: 'pixelated',
      }}
    />
  );
}

/** A key on either the transform track of one layer, or the shared audio
 * track - a single selection model across a variable number of lanes (as
 * many as there are layers, plus one for audio). */
type SelectedKey =
  | { track: 'layer'; layerId: string; index: number }
  | { track: 'audio'; index: number };

export function IntroRoute() {
  const { data: storedDoc, setData: saveDoc, loading: docLoading } =
    useModeDocument<IntroSequenceLibraryDoc>('introsequences', INTRO_LIBRARY_DOC_FALLBACK);
  const { data: pixelDoc } = useModeDocument<PixelSpriteSource>('pixel', PIXEL_SPRITE_SOURCE_FALLBACK);
  const sprites = useMemo(() => pixelDoc.sprites ?? {}, [pixelDoc.sprites]);

  const [sel, setSel] = useState(0);
  const [selectedKey, setSelectedKey] = useState<SelectedKey | null>(null);
  const [selectedLayerId, setSelectedLayerId] = useState<string | null>(null);
  const [previewT, setPreviewT] = useState(0);
  const [previewPlaying, setPreviewPlaying] = useState(false);
  const hydrated = useRef(false);
  const [sequences, setSequencesState] = useState<IntroSequence[]>([]);
  const [canvasSize, setCanvasSizeState] = useState<Record<string, { width: number; height: number }>>({});
  if (!docLoading && !hydrated.current) {
    hydrated.current = true;
    setSequencesState(storedDoc.sequences);
    setCanvasSizeState(storedDoc.canvasSize ?? {});
  }

  // setSequencesState only ever computes the next value here - React may
  // call a state updater more than once per update (Strict Mode, concurrent
  // rendering), so scheduling the network-bound saveDoc from inside one
  // would be a purity violation. The actual save is a separate effect below
  // (same reasoning CutsceneTimeline documents for its own setCutscenes).
  const setSequences = (next: IntroSequence[] | ((list: IntroSequence[]) => IntroSequence[])) => {
    setSequencesState((list) => (typeof next === 'function' ? next(list) : next));
  };

  // Every sequence must clear validateIntroSequence (positive duration,
  // every transform/audio key within [0, duration], no transform track for a
  // layer id that isn't in `layers`) before this document goes to the
  // backend at all.
  useEffect(() => {
    if (!hydrated.current) return;
    const allValid = sequences.every((s) => {
      try {
        validateIntroSequence(s);
        return true;
      } catch {
        return false;
      }
    });
    if (!allValid) return;
    saveDoc({ sequences, canvasSize });
  }, [sequences, canvasSize, saveDoc]);

  const sequence = sequences[sel] as IntroSequence | undefined;
  let validationError: string | null = null;
  if (sequence) {
    try {
      validateIntroSequence(sequence);
    } catch (err) {
      validationError = err instanceof Error ? err.message : 'Invalid intro sequence.';
    }
  }

  useEffect(() => {
    setPreviewT(0);
    setPreviewPlaying(false);
    setSelectedKey(null);
    setSelectedLayerId(null);
  }, [sequence?.id]);

  // Keep the scrubber in range if an edit shrinks the sequence below the
  // current playhead.
  useEffect(() => {
    if (sequence && previewT > sequence.duration) setPreviewT(sequence.duration);
  }, [sequence, previewT]);

  // A minimal own clock for the Play button: advances previewT by real
  // elapsed time until the sequence's duration, then stops. Deliberately not
  // `stepIntro`/`IntroPlayer` - this only ever drives the same `previewT`
  // state the scrubber itself controls, so play and scrub are the same knob.
  useEffect(() => {
    if (!previewPlaying || !sequence) return;
    let raf = 0;
    let last: number | null = null;
    const duration = sequence.duration;
    function tick(ts: number) {
      if (last === null) {
        last = ts;
        raf = requestAnimationFrame(tick);
        return;
      }
      const dt = (ts - last) / 1000;
      last = ts;
      setPreviewT((t) => {
        const nextT = Math.min(duration, t + dt);
        if (nextT >= duration) setPreviewPlaying(false);
        return nextT;
      });
      raf = requestAnimationFrame(tick);
    }
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [previewPlaying, sequence]);

  function addSequence() {
    const id = uniqueId('intro', sequences.map((s) => s.id));
    setSequences((list) => [...list, blankIntroSequence(id)]);
    setSel(sequences.length);
    setSelectedKey(null);
    setSelectedLayerId(null);
  }

  const [pendingDeleteIndex, setPendingDeleteIndex] = useState<number | null>(null);
  function removeSequence(index: number) {
    const removedId = sequences[index]?.id;
    setSequences((list) => list.filter((_, i) => i !== index));
    if (removedId) {
      setCanvasSizeState((sizes) => {
        const next = { ...sizes };
        delete next[removedId];
        return next;
      });
    }
    setSel((s) => Math.max(0, s >= index ? s - 1 : s));
    setSelectedKey(null);
    setSelectedLayerId(null);
  }

  function patchSequence(patch: Partial<IntroSequence>) {
    setSequences((list) => list.map((s, i) => (i === sel ? { ...s, ...patch } : s)));
  }

  /** The id is the presumed reference point for anything that resolves this
   * sequence (a game's boot-sequence loader, the exported JSON's own
   * filename), so a rename is deliberate and validated rather than a plain
   * text field - same contract as CutsceneTimeline's renameCutscene. */
  function renameSequence(rawId: string) {
    if (!sequence) return;
    const nextId = rawId.trim();
    if (!nextId || nextId === sequence.id) return;
    if (sequences.some((s) => s.id !== sequence.id && s.id === nextId)) return;
    setCanvasSizeState((sizes) => {
      const existing = sizes[sequence.id];
      if (!existing) return sizes;
      const next = { ...sizes };
      delete next[sequence.id];
      next[nextId] = existing;
      return next;
    });
    patchSequence({ id: nextId });
  }

  function patchTracks(patch: Partial<IntroSequence['tracks']>) {
    if (!sequence) return;
    patchSequence({ tracks: { ...sequence.tracks, ...patch } });
  }

  function patchCanvasSize(patch: Partial<{ width: number; height: number }>) {
    if (!sequence) return;
    setCanvasSizeState((sizes) => ({
      ...sizes,
      [sequence.id]: { ...(sizes[sequence.id] ?? DEFAULT_INTRO_CANVAS_SIZE), ...patch },
    }));
  }

  const activeCanvasSize = sequence ? (canvasSize[sequence.id] ?? DEFAULT_INTRO_CANVAS_SIZE) : DEFAULT_INTRO_CANVAS_SIZE;
  const frames = useMemo(() => (sequence ? resolveFrames(sequence, sprites) : {}), [sequence, sprites]);

  return (
    <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
      <M2Panel side="left" width={220}>
        <M2Label action={
          <button className="ss-btn ss-btn--ghost" onClick={addSequence} style={{ padding: '3px 9px', fontSize: 11, borderRadius: 6 }}>+ New</button>
        }>Intro Sequences</M2Label>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, padding: '0 8px' }}>
          {sequences.length === 0 && (
            <span style={{ padding: '10px 8px', fontSize: 12, color: 'var(--ss-text-3)' }}>No intro sequences yet.</span>
          )}
          {sequences.map((s, i) => (
            <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <button
                onClick={() => { setSel(i); setSelectedKey(null); setSelectedLayerId(null); }}
                style={{
                  flex: 1, textAlign: 'left', padding: '7px 9px', borderRadius: 7, fontSize: 12.5,
                  background: i === sel ? 'var(--ss-accent-soft)' : 'transparent',
                  color: i === sel ? 'var(--ss-accent)' : 'var(--ss-text-2)', border: 'none', cursor: 'pointer',
                }}
              >{s.id}</button>
              <button
                onClick={() => setPendingDeleteIndex(i)}
                aria-label={`Delete ${s.id}`}
                title="Delete"
                style={{ padding: '4px 6px', fontSize: 11, color: 'var(--ss-text-3)', background: 'none', border: 'none', cursor: 'pointer' }}
              >×</button>
            </div>
          ))}
        </div>
      </M2Panel>

      {pendingDeleteIndex !== null && (
        <EdConfirmDialog
          title={`Delete "${sequences[pendingDeleteIndex]?.id}"?`}
          body="This permanently removes every layer, track and key in this intro sequence. There is no undo."
          confirmLabel="Delete"
          onConfirm={() => { removeSequence(pendingDeleteIndex); setPendingDeleteIndex(null); }}
          onCancel={() => setPendingDeleteIndex(null)}
        />
      )}

      <div style={{ flex: 1, overflowY: 'auto', minWidth: 0 }}>
        {!sequence ? (
          <div style={{ padding: 40, color: 'var(--ss-text-3)', fontSize: 13 }}>
            Select an intro sequence, or create one to start authoring its sprite, text and particle layers.
          </div>
        ) : (
          <div style={{ maxWidth: 900, margin: '0 auto', padding: '32px 36px 72px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20, flexWrap: 'wrap' }}>
              <input
                key={sequence.id}
                className="ss-input"
                defaultValue={sequence.id}
                title="Sequence id - what a game's boot loader (and the exported JSON's filename) refers to it by"
                onBlur={(e) => renameSequence(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
                style={{ fontFamily: 'var(--ss-display)', fontSize: 20, fontWeight: 500, padding: '4px 9px', borderRadius: 8, width: 220 }}
              />
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--ss-text-3)' }}>
                Duration
                <input
                  className="ss-input" type="number" min={0.1} step={0.1}
                  value={sequence.duration}
                  onChange={(e) => {
                    const raw = Number(e.target.value);
                    if (!Number.isFinite(raw)) return;
                    patchSequence({ duration: Math.max(0.1, raw) });
                  }}
                  style={{ width: 64, padding: '4px 8px', fontSize: 12 }}
                />
                s
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--ss-text-3)' }}>
                <button role="switch" aria-checked={sequence.skippable} className={'ss-switch' + (sequence.skippable ? ' is-on' : '')} onClick={() => patchSequence({ skippable: !sequence.skippable })} aria-label="Skippable" />
                Skippable
              </label>
              {sequence.skippable && (
                <label
                  style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--ss-text-3)' }}
                  title="Where a skip lands: typically the start of the outro fade, so a skipped sequence still fades out rather than cutting. Leave blank to jump straight to the end."
                >
                  Skip to
                  <input
                    className="ss-input" type="number" min={0} max={sequence.duration} step={0.1}
                    value={sequence.skipTo ?? ''}
                    placeholder="end"
                    onChange={(e) => {
                      if (e.target.value === '') { patchSequence({ skipTo: undefined }); return; }
                      const raw = Number(e.target.value);
                      if (!Number.isFinite(raw)) return;
                      patchSequence({ skipTo: Math.max(0, Math.min(sequence.duration, raw)) });
                    }}
                    style={{ width: 64, padding: '4px 8px', fontSize: 12 }}
                  />
                  s
                </label>
              )}
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--ss-text-3)' }}>
                Canvas
                <input
                  className="ss-input" type="number" min={16} step={1} value={activeCanvasSize.width}
                  onChange={(e) => { const v = Math.round(Number(e.target.value)); if (v >= 16) patchCanvasSize({ width: v }); }}
                  style={{ width: 54, padding: '4px 6px', fontSize: 12 }}
                />
                ×
                <input
                  className="ss-input" type="number" min={16} step={1} value={activeCanvasSize.height}
                  onChange={(e) => { const v = Math.round(Number(e.target.value)); if (v >= 16) patchCanvasSize({ height: v }); }}
                  style={{ width: 54, padding: '4px 6px', fontSize: 12 }}
                />
              </label>
              <button
                className="ss-btn ss-btn--primary ss-btn--sm"
                disabled={!!validationError}
                onClick={() => downloadIntroSequenceJson(sequence, sprites, activeCanvasSize)}
                style={{ marginLeft: 'auto' }}
              >Download JSON</button>
            </div>

            {validationError && (
              <div style={{ padding: '8px 12px', marginBottom: 16, borderRadius: 8, background: 'rgba(220,80,80,0.12)', color: 'var(--ss-danger, #e06060)', fontSize: 12.5 }}>
                {validationError}
              </div>
            )}

            <div style={{ marginBottom: 16 }}>
              <IntroPreviewCanvas
                sequence={sequence}
                frames={frames}
                t={previewT}
                width={activeCanvasSize.width}
                height={activeCanvasSize.height}
              />
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10 }}>
                <button
                  className="ss-btn ss-btn--ghost ss-btn--sm"
                  onClick={() => setPreviewPlaying((p) => !p)}
                >{previewPlaying ? '⏸ Pause' : '▶ Play'}</button>
                <button
                  className="ss-btn ss-btn--ghost ss-btn--sm"
                  onClick={() => { setPreviewT(0); setPreviewPlaying(false); }}
                >⏮ Restart</button>
                <input
                  type="range" min={0} max={Math.max(sequence.duration, 0.001)} step={0.01} value={previewT}
                  onChange={(e) => { setPreviewPlaying(false); setPreviewT(Number(e.target.value)); }}
                  style={{ flex: 1 }}
                  className="ed-range"
                />
                <span style={{ fontSize: 11, color: 'var(--ss-text-3)', fontVariantNumeric: 'tabular-nums', width: 84, textAlign: 'right' }}>
                  {previewT.toFixed(2)}s / {sequence.duration.toFixed(2)}s
                </span>
              </div>
            </div>

            <IntroTimelineTracks
              sequence={sequence}
              selectedKey={selectedKey}
              onSelectKey={setSelectedKey}
              onPatchTracks={patchTracks}
              playheadT={previewT}
              onScrub={(t) => { setPreviewPlaying(false); setPreviewT(t); }}
              selectedLayerId={selectedLayerId}
              onSelectLayer={setSelectedLayerId}
            />

            {selectedKey && (
              <IntroKeyEditor
                sequence={sequence}
                selected={selectedKey}
                onPatchTracks={patchTracks}
                onSelectKey={setSelectedKey}
                onDeselect={() => setSelectedKey(null)}
              />
            )}
          </div>
        )}
      </div>

      {sequence && (
        <LayersPanel
          sequence={sequence}
          selectedLayerId={selectedLayerId}
          onSelectLayer={setSelectedLayerId}
          onPatchSequence={patchSequence}
          sprites={sprites}
        />
      )}
    </div>
  );
}

function IntroTimelineTracks({
  sequence, selectedKey, onSelectKey, onPatchTracks, playheadT, onScrub, selectedLayerId, onSelectLayer,
}: {
  sequence: IntroSequence;
  selectedKey: SelectedKey | null;
  onSelectKey: (key: SelectedKey) => void;
  onPatchTracks: (patch: Partial<IntroSequence['tracks']>) => void;
  playheadT: number;
  onScrub: (t: number) => void;
  selectedLayerId: string | null;
  onSelectLayer: (id: string) => void;
}) {
  const rulerRef = useRef<HTMLDivElement>(null);

  function timeFromClientX(clientX: number): number {
    const el = rulerRef.current;
    if (!el) return 0;
    const rect = el.getBoundingClientRect();
    const frac = rect.width > 0 ? (clientX - rect.left) / rect.width : 0;
    const t = Math.max(0, Math.min(sequence.duration, frac * sequence.duration));
    return Math.round(t * 20) / 20; // snap to 0.05s
  }

  // A newly added transform key deliberately carries only `t` - a true
  // no-op keyframe (sampleLayerTransform holds whatever came before it)
  // rather than baking in the currently-sampled values. That keeps the
  // schema's "sparse, cumulative" contract intact: an earlier key's later
  // edit still cascades through a downstream key that never itself set that
  // field, matching the doc comment on `TransformKey`.
  function addTransformKeyAt(layerId: string, t: number) {
    const existing = sequence.tracks.transform[layerId] ?? [];
    const key: TransformKey = { t };
    const sorted = [...existing, key].sort((a, b) => a.t - b.t);
    onPatchTracks({ transform: { ...sequence.tracks.transform, [layerId]: sorted } });
    onSelectKey({ track: 'layer', layerId, index: sorted.indexOf(key) });
  }

  function addAudioKeyAt(t: number) {
    const key: AudioCue = { t, cue: 'cue' };
    const sorted = [...sequence.tracks.audio, key].sort((a, b) => a.t - b.t);
    onPatchTracks({ audio: sorted });
    onSelectKey({ track: 'audio', index: sorted.indexOf(key) });
  }

  const playheadLeft = sequence.duration > 0 ? (playheadT / sequence.duration) * 100 : 0;

  return (
    <div style={{ position: 'relative', border: '1px solid var(--ss-border-soft)', borderRadius: 10, overflow: 'hidden' }}>
      <div
        ref={rulerRef}
        data-testid="intro-ruler"
        role="slider"
        aria-label="Playhead"
        aria-valuemin={0}
        aria-valuemax={sequence.duration}
        aria-valuenow={playheadT}
        onClick={(e) => onScrub(timeFromClientX(e.clientX))}
        style={{ position: 'relative', height: 22, background: 'rgba(255,255,255,0.03)', borderBottom: '1px solid var(--ss-border-soft)', cursor: 'pointer' }}
      >
        {Array.from({ length: Math.floor(sequence.duration) + 1 }, (_, s) => (
          <span key={s} style={{
            position: 'absolute', left: `${sequence.duration > 0 ? (s / sequence.duration) * 100 : 0}%`,
            top: 3, fontSize: 9, color: 'var(--ss-text-3)', transform: 'translateX(-50%)', pointerEvents: 'none',
          }}>{s}s</span>
        ))}
      </div>
      {/* playhead - spans the ruler and every layer/audio lane */}
      <div style={{
        position: 'absolute', top: 0, bottom: 0, left: `${playheadLeft}%`, width: 1.5,
        background: 'var(--ss-accent)', pointerEvents: 'none', zIndex: 1,
        boxShadow: '0 0 6px var(--ss-accent-border)',
      }} />
      <div style={{
        position: 'absolute', top: 0, left: `${playheadLeft}%`, transform: 'translateX(-50%)',
        width: 0, height: 0, borderLeft: '4px solid transparent', borderRight: '4px solid transparent',
        borderTop: '5px solid var(--ss-accent)', pointerEvents: 'none', zIndex: 1,
      }} />
      {sequence.layers.length === 0 && (
        <div style={{ padding: '14px 12px', fontSize: 12, color: 'var(--ss-text-3)' }}>No layers yet - add one from the Layers panel.</div>
      )}
      {sequence.layers.map((layer) => {
        const keys = sequence.tracks.transform[layer.id] ?? [];
        const isLayerSelected = selectedLayerId === layer.id;
        return (
          <div key={layer.id} style={{ display: 'flex', borderBottom: '1px solid var(--ss-border-soft)' }}>
            <button
              onClick={() => onSelectLayer(layer.id)}
              title={layer.id}
              style={{
                width: 104, flexShrink: 0, textAlign: 'left', padding: '8px 10px', fontSize: 11,
                color: isLayerSelected ? 'var(--ss-accent)' : 'var(--ss-text-3)',
                background: isLayerSelected ? 'var(--ss-accent-soft)' : 'transparent', border: 'none',
                textTransform: 'uppercase', letterSpacing: '0.06em', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'pointer',
              }}
            >{layer.id}</button>
            <div
              role="button"
              tabIndex={0}
              aria-label={`Add a ${layer.id} key`}
              onClick={(e) => {
                if (e.target !== e.currentTarget) return;
                addTransformKeyAt(layer.id, timeFromClientX(e.clientX));
              }}
              style={{ position: 'relative', flex: 1, height: 30, cursor: 'copy' }}
            >
              {keys.map((key, i) => {
                const isSelected = selectedKey?.track === 'layer' && selectedKey.layerId === layer.id && selectedKey.index === i;
                const left = sequence.duration > 0 ? (key.t / sequence.duration) * 100 : 0;
                return (
                  <button
                    key={i}
                    onClick={(e) => { e.stopPropagation(); onSelectKey({ track: 'layer', layerId: layer.id, index: i }); }}
                    title={`t=${key.t}s`}
                    style={{
                      position: 'absolute', left: `${left}%`, top: '50%', transform: 'translate(-50%, -50%)',
                      width: 11, height: 11, borderRadius: 3, cursor: 'pointer',
                      background: isSelected ? 'var(--ss-accent)' : 'var(--ss-text-2)',
                      border: '1px solid var(--ss-bg-1)',
                    }}
                  />
                );
              })}
            </div>
          </div>
        );
      })}
      <div style={{ display: 'flex' }}>
        <div style={{ width: 104, flexShrink: 0, padding: '8px 10px', fontSize: 11, color: 'var(--ss-text-3)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Audio</div>
        <div
          role="button"
          tabIndex={0}
          aria-label="Add an audio key"
          onClick={(e) => {
            if (e.target !== e.currentTarget) return;
            addAudioKeyAt(timeFromClientX(e.clientX));
          }}
          style={{ position: 'relative', flex: 1, height: 30, cursor: 'copy' }}
        >
          {sequence.tracks.audio.map((cue, i) => {
            const isSelected = selectedKey?.track === 'audio' && selectedKey.index === i;
            const left = sequence.duration > 0 ? (cue.t / sequence.duration) * 100 : 0;
            return (
              <button
                key={i}
                onClick={(e) => { e.stopPropagation(); onSelectKey({ track: 'audio', index: i }); }}
                title={`t=${cue.t}s`}
                style={{
                  position: 'absolute', left: `${left}%`, top: '50%', transform: 'translate(-50%, -50%)',
                  width: 11, height: 11, borderRadius: 3, cursor: 'pointer',
                  background: isSelected ? 'var(--ss-accent)' : 'var(--ss-text-2)',
                  border: '1px solid var(--ss-bg-1)',
                }}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}

function IntroKeyEditor({
  sequence, selected, onPatchTracks, onSelectKey, onDeselect,
}: {
  sequence: IntroSequence;
  selected: SelectedKey;
  onPatchTracks: (patch: Partial<IntroSequence['tracks']>) => void;
  onSelectKey: (key: SelectedKey) => void;
  onDeselect: () => void;
}) {
  function removeKey() {
    if (selected.track === 'audio') {
      onPatchTracks({ audio: sequence.tracks.audio.filter((_, i) => i !== selected.index) });
    } else {
      const list = sequence.tracks.transform[selected.layerId] ?? [];
      onPatchTracks({ transform: { ...sequence.tracks.transform, [selected.layerId]: list.filter((_, i) => i !== selected.index) } });
    }
    onDeselect();
  }

  // Re-sort after every patch, since `t` (the sort key) can change here -
  // both tracks must stay sorted ascending by `t` for sampleLayerTransform/
  // dueAudioCues to sample them correctly. The patched key is tracked by
  // identity so selection follows it to its new index.
  function patchKey(patch: Record<string, unknown>) {
    if (selected.track === 'audio') {
      const list = sequence.tracks.audio;
      const patched = { ...list[selected.index], ...patch } as AudioCue;
      const next = list.map((k, i) => (i === selected.index ? patched : k)).sort((a, b) => a.t - b.t);
      onPatchTracks({ audio: next });
      const newIndex = next.indexOf(patched);
      if (newIndex !== selected.index) onSelectKey({ track: 'audio', index: newIndex });
    } else {
      const list = sequence.tracks.transform[selected.layerId] ?? [];
      const patched = { ...list[selected.index], ...patch } as TransformKey;
      const next = list.map((k, i) => (i === selected.index ? patched : k)).sort((a, b) => a.t - b.t);
      onPatchTracks({ transform: { ...sequence.tracks.transform, [selected.layerId]: next } });
      const newIndex = next.indexOf(patched);
      if (newIndex !== selected.index) onSelectKey({ track: 'layer', layerId: selected.layerId, index: newIndex });
    }
  }

  const key = selected.track === 'audio'
    ? sequence.tracks.audio[selected.index]
    : (sequence.tracks.transform[selected.layerId] ?? [])[selected.index];
  if (!key) return null;

  return (
    <div style={{ marginTop: 16, padding: 16, borderRadius: 10, background: 'var(--ss-bg-2)', border: '1px solid var(--ss-border-soft)' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <span style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--ss-text-3)' }}>
          {selected.track === 'audio' ? 'audio' : selected.layerId} key
        </span>
        <button className="ss-btn ss-btn--ghost" onClick={removeKey} style={{ padding: '3px 9px', fontSize: 11, borderRadius: 6 }}>Delete</button>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
        <FieldNumber label="t (s)" value={key.t} min={0} max={sequence.duration} step={0.05} onChange={(v) => patchKey({ t: v })} />
        {selected.track === 'audio'
          ? <FieldText label="Cue" value={(key as AudioCue).cue} onChange={(v) => patchKey({ cue: v })} />
          : <TransformKeyFields keyValue={key as TransformKey} onChange={patchKey} />}
      </div>
    </div>
  );
}

function TransformKeyFields({ keyValue, onChange }: { keyValue: TransformKey; onChange: (patch: Record<string, unknown>) => void }) {
  return (
    <>
      <FieldNumber label="x" value={keyValue.x ?? DEFAULT_TRANSFORM.x} step={1} onChange={(v) => onChange({ x: v })} />
      <FieldNumber label="y" value={keyValue.y ?? DEFAULT_TRANSFORM.y} step={1} onChange={(v) => onChange({ y: v })} />
      <FieldNumber label="Scale" value={keyValue.scale ?? DEFAULT_TRANSFORM.scale} step={0.05} onChange={(v) => onChange({ scale: v })} />
      <FieldNumber label="Rotation (rad)" value={keyValue.rotation ?? DEFAULT_TRANSFORM.rotation} step={0.05} onChange={(v) => onChange({ rotation: v })} />
      <FieldNumber label="Opacity" value={keyValue.opacity ?? DEFAULT_TRANSFORM.opacity} min={0} max={1} step={0.05} onChange={(v) => onChange({ opacity: v })} />
      <FieldSelect
        label="Easing" value={keyValue.easing ?? 'linear'}
        options={[['linear', 'Linear'], ['easeIn', 'Ease in'], ['easeOut', 'Ease out'], ['easeInOut', 'Ease in/out']]}
        onChange={(v) => onChange({ easing: v as Easing })}
      />
    </>
  );
}

/* ── Layers panel: add/remove/reorder layers, edit the selected layer's own
 * fields. Cutscene has five fixed tracks and never needed this - an intro
 * sequence's layer list is author-defined, so this file owns the whole
 * lifecycle. ──────────────────────────────────────────────────────────── */

function LayersPanel({
  sequence, selectedLayerId, onSelectLayer, onPatchSequence, sprites,
}: {
  sequence: IntroSequence;
  selectedLayerId: string | null;
  onSelectLayer: (id: string | null) => void;
  onPatchSequence: (patch: Partial<IntroSequence>) => void;
  sprites: Record<string, PixelSpriteDoc>;
}) {
  function addLayer(kind: IntroLayer['kind']) {
    const id = uniqueId(kind, sequence.layers.map((l) => l.id));
    const layer: IntroLayer = kind === 'sprite'
      ? { kind: 'sprite', id, sprite: { spriteId: Object.keys(sprites)[0] ?? '' }, pixelSize: 4 }
      : kind === 'text'
        ? { kind: 'text', id, text: 'Text', font: "700 32px 'Press Start 2P', monospace", color: '#ffffff', align: 'center' }
        : kind === 'fill'
          ? { kind: 'fill', id, color: '#1c1c1c' }
          : kind === 'image'
            ? { kind: 'image', id, imageId: '', width: 128, height: 128 }
            : { kind: 'particles', id, preset: 'sparkle', seed: 1, count: 24, color: '#ffffff' };
    // A backdrop belongs underneath everything else: layers paint in list
    // order, so a fill added on top of existing layers would hide them all.
    onPatchSequence({ layers: kind === 'fill' ? [layer, ...sequence.layers] : [...sequence.layers, layer] });
    onSelectLayer(id);
  }

  function removeLayer(id: string) {
    const nextTransform = { ...sequence.tracks.transform };
    delete nextTransform[id];
    onPatchSequence({
      layers: sequence.layers.filter((l) => l.id !== id),
      tracks: { ...sequence.tracks, transform: nextTransform },
    });
    if (selectedLayerId === id) onSelectLayer(null);
  }

  function moveLayer(id: string, dir: -1 | 1) {
    const idx = sequence.layers.findIndex((l) => l.id === id);
    const swapWith = idx + dir;
    if (idx < 0 || swapWith < 0 || swapWith >= sequence.layers.length) return;
    const next = [...sequence.layers];
    const a = next[idx]!;
    const b = next[swapWith]!;
    next[idx] = b;
    next[swapWith] = a;
    onPatchSequence({ layers: next });
  }

  function patchLayer(id: string, patch: Partial<IntroLayer>) {
    onPatchSequence({ layers: sequence.layers.map((l) => (l.id === id ? ({ ...l, ...patch } as IntroLayer) : l)) });
  }

  const selected = sequence.layers.find((l) => l.id === selectedLayerId) ?? null;

  return (
    <M2Panel side="right" width={260}>
      <M2Label>Layers</M2Label>
      <div style={{ display: 'flex', gap: 6, padding: '0 16px 10px' }}>
        <button className="ss-btn ss-btn--ghost ss-btn--sm" onClick={() => addLayer('sprite')} style={{ flex: 1, fontSize: 11, padding: '5px 6px' }}>+ Sprite</button>
        <button className="ss-btn ss-btn--ghost ss-btn--sm" onClick={() => addLayer('text')} style={{ flex: 1, fontSize: 11, padding: '5px 6px' }}>+ Text</button>
        <button className="ss-btn ss-btn--ghost ss-btn--sm" onClick={() => addLayer('particles')} style={{ flex: 1, fontSize: 11, padding: '5px 6px' }}>+ Particles</button>
        <button className="ss-btn ss-btn--ghost ss-btn--sm" onClick={() => addLayer('fill')} style={{ flex: 1, fontSize: 11, padding: '5px 6px' }} title="A full-canvas backdrop colour; only its opacity track applies">+ Fill</button>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, padding: '0 8px' }}>
        {sequence.layers.length === 0 && (
          <span style={{ padding: '10px 8px', fontSize: 12, color: 'var(--ss-text-3)' }}>No layers yet.</span>
        )}
        {sequence.layers.map((layer, i) => (
          <div key={layer.id} style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <button
              onClick={() => onSelectLayer(layer.id)}
              style={{
                flex: 1, textAlign: 'left', padding: '7px 9px', borderRadius: 7, fontSize: 12.5,
                background: layer.id === selectedLayerId ? 'var(--ss-accent-soft)' : 'transparent',
                color: layer.id === selectedLayerId ? 'var(--ss-accent)' : 'var(--ss-text-2)', border: 'none', cursor: 'pointer',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}
            >{layer.id} <span style={{ color: 'var(--ss-text-3)', fontSize: 10.5 }}>· {layer.kind}</span></button>
            <button
              onClick={() => moveLayer(layer.id, -1)} disabled={i === 0}
              aria-label={`Move ${layer.id} up`} title="Move up"
              style={{ padding: '4px 5px', fontSize: 11, color: 'var(--ss-text-3)', background: 'none', border: 'none', cursor: i === 0 ? 'default' : 'pointer', opacity: i === 0 ? 0.35 : 1 }}
            >↑</button>
            <button
              onClick={() => moveLayer(layer.id, 1)} disabled={i === sequence.layers.length - 1}
              aria-label={`Move ${layer.id} down`} title="Move down"
              style={{ padding: '4px 5px', fontSize: 11, color: 'var(--ss-text-3)', background: 'none', border: 'none', cursor: i === sequence.layers.length - 1 ? 'default' : 'pointer', opacity: i === sequence.layers.length - 1 ? 0.35 : 1 }}
            >↓</button>
            <button
              onClick={() => removeLayer(layer.id)}
              aria-label={`Delete ${layer.id}`} title="Delete"
              style={{ padding: '4px 6px', fontSize: 11, color: 'var(--ss-text-3)', background: 'none', border: 'none', cursor: 'pointer' }}
            >×</button>
          </div>
        ))}
      </div>
      {selected && (
        <>
          <M2Label>{LAYER_KIND_LABELS[selected.kind]}</M2Label>
          <div style={{ padding: '0 16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
            {selected.kind === 'sprite' && <SpriteLayerFields layer={selected} sprites={sprites} onChange={(patch) => patchLayer(selected.id, patch)} />}
            {selected.kind === 'text' && <TextLayerFields layer={selected} onChange={(patch) => patchLayer(selected.id, patch)} />}
            {selected.kind === 'particles' && <ParticleLayerFields layer={selected} onChange={(patch) => patchLayer(selected.id, patch)} />}
            {selected.kind === 'fill' && <FillLayerFields layer={selected} onChange={(patch) => patchLayer(selected.id, patch)} />}
            {selected.kind === 'image' && (
              <span style={{ fontSize: 11, color: 'var(--ss-text-3)' }}>
                Image layer ({selected.imageId || 'no image id'}, {selected.width}x{selected.height}). Image layers are for a studio or game's own pre-existing brand asset and are wired up in code, not authored here.
              </span>
            )}
          </div>
        </>
      )}
    </M2Panel>
  );
}

const LAYER_KIND_LABELS: Record<IntroLayer['kind'], string> = {
  sprite: 'Sprite layer',
  text: 'Text layer',
  particles: 'Particle layer',
  image: 'Image layer',
  fill: 'Fill layer',
};

function SpriteLayerFields({ layer, sprites, onChange }: {
  layer: SpriteLayer; sprites: Record<string, PixelSpriteDoc>; onChange: (patch: Partial<SpriteLayer>) => void;
}) {
  const spriteNames = Object.keys(sprites);
  const spriteDoc = sprites[layer.sprite.spriteId];
  const clipNames = spriteDoc ? Object.keys(spriteDoc.clips) : [];
  return (
    <>
      <PanelField label="Sprite">
        <select
          className="ss-input" value={layer.sprite.spriteId}
          onChange={(e) => onChange({ sprite: { spriteId: e.target.value, clip: undefined } })}
          style={{ width: '100%', padding: '6px 8px', fontSize: 12 }}
        >
          <option value="">Choose a sprite…</option>
          {spriteNames.map((n) => <option key={n} value={n}>{n}</option>)}
        </select>
      </PanelField>
      {spriteDoc && (
        <PanelField label="Clip">
          <select
            className="ss-input" value={layer.sprite.clip ?? ''}
            onChange={(e) => onChange({ sprite: { ...layer.sprite, clip: e.target.value || undefined } })}
            style={{ width: '100%', padding: '6px 8px', fontSize: 12 }}
          >
            <option value="">Active clip ({spriteDoc.activeClip})</option>
            {clipNames.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </PanelField>
      )}
      <PanelField label="Pixel size">
        <input
          className="ss-input" type="number" min={1} step={1} value={layer.pixelSize ?? 1}
          onChange={(e) => { const v = Math.round(Number(e.target.value)); if (v >= 1) onChange({ pixelSize: v }); }}
          style={{ width: '100%', padding: '6px 8px', fontSize: 12 }}
        />
      </PanelField>
    </>
  );
}

function TextLayerFields({ layer, onChange }: { layer: TextLayer; onChange: (patch: Partial<TextLayer>) => void }) {
  return (
    <>
      <PanelField label="Text">
        <input className="ss-input" type="text" value={layer.text} onChange={(e) => onChange({ text: e.target.value })} style={{ width: '100%', padding: '6px 8px', fontSize: 12 }} />
      </PanelField>
      <PanelField label="Font (CSS shorthand)">
        <input className="ss-input" type="text" value={layer.font} onChange={(e) => onChange({ font: e.target.value })} style={{ width: '100%', padding: '6px 8px', fontSize: 12 }} />
      </PanelField>
      <PanelField label="Colour">
        <input className="ss-input" type="text" value={layer.color} onChange={(e) => onChange({ color: e.target.value })} style={{ width: '100%', padding: '6px 8px', fontSize: 12 }} />
      </PanelField>
      <PanelField label="Align">
        <select
          className="ss-input" value={layer.align ?? 'left'}
          onChange={(e) => onChange({ align: e.target.value as TextLayer['align'] })}
          style={{ width: '100%', padding: '6px 8px', fontSize: 12 }}
        >
          <option value="left">Left</option>
          <option value="center">Center</option>
          <option value="right">Right</option>
        </select>
      </PanelField>
      <PanelField label="Letter spacing (px)">
        <input
          className="ss-input" type="number" min={0} step={0.5} value={layer.letterSpacing ?? 0}
          onChange={(e) => { const v = Number(e.target.value); if (Number.isFinite(v)) onChange({ letterSpacing: v > 0 ? v : undefined }); }}
          style={{ width: '100%', padding: '6px 8px', fontSize: 12 }}
        />
      </PanelField>
    </>
  );
}

function FillLayerFields({ layer, onChange }: { layer: FillLayer; onChange: (patch: Partial<FillLayer>) => void }) {
  return (
    <>
      <PanelField label="Colour">
        <input className="ss-input" type="text" value={layer.color} onChange={(e) => onChange({ color: e.target.value })} style={{ width: '100%', padding: '6px 8px', fontSize: 12 }} />
      </PanelField>
      <span style={{ fontSize: 11, color: 'var(--ss-text-3)' }}>
        Covers the whole canvas. Only this layer's opacity keys apply; position, scale and rotation are ignored.
      </span>
    </>
  );
}

function ParticleLayerFields({ layer, onChange }: { layer: ParticleLayer; onChange: (patch: Partial<ParticleLayer>) => void }) {
  return (
    <>
      <PanelField label="Preset">
        <select
          className="ss-input" value={layer.preset}
          onChange={(e) => onChange({ preset: e.target.value as ParticlePreset })}
          style={{ width: '100%', padding: '6px 8px', fontSize: 12 }}
        >
          <option value="sparkle">Sparkle</option>
          <option value="dust">Dust</option>
          <option value="burst">Burst</option>
        </select>
      </PanelField>
      <PanelField label="Seed">
        <input className="ss-input" type="number" step={1} value={layer.seed} onChange={(e) => onChange({ seed: Math.round(Number(e.target.value)) })} style={{ width: '100%', padding: '6px 8px', fontSize: 12 }} />
      </PanelField>
      <PanelField label="Count">
        <input
          className="ss-input" type="number" min={1} max={500} step={1} value={layer.count}
          onChange={(e) => { const v = Math.round(Number(e.target.value)); if (v >= 1) onChange({ count: v }); }}
          style={{ width: '100%', padding: '6px 8px', fontSize: 12 }}
        />
      </PanelField>
      <PanelField label="Colour">
        <input className="ss-input" type="text" value={layer.color} onChange={(e) => onChange({ color: e.target.value })} style={{ width: '100%', padding: '6px 8px', fontSize: 12 }} />
      </PanelField>
    </>
  );
}

function PanelField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 11, color: 'var(--ss-text-3)' }}>
      {label}
      {children}
    </label>
  );
}

/* Small generic field controls (FieldNumber/FieldText/FieldSelect) now live
 * in fieldPrimitives.tsx, shared with CutsceneTimeline.tsx - see that
 * module's own doc comment. */
